import "./src/styles/global.css";
import React from "react";
import type { GatsbyBrowser } from "gatsby";

import { Amplify } from "aws-amplify";

import { isAstroPath } from "./src/constants/astroMigration";
import { AuthProvider } from "./src/contexts/AuthContext";
import { loadUser } from "./src/services/gacUser/gacUserService";
import Layout from "./src/components/Layout";

export const wrapPageElement: GatsbyBrowser["wrapPageElement"] = ({ element }) => (
  <Layout>
    <AuthProvider>{element}</AuthProvider>
  </Layout>
);

export const wrapRootElement: GatsbyBrowser["wrapRootElement"] = ({ element }) => element;

// AuthProvider's own auth check only runs once, on first mount, and Gatsby's router
// intentionally keeps it mounted across internal navigation (wrapPageElement) - so it never
// re-checks the shared session again on its own. Astro and this app run on the same origin and
// both read/write the Cognito tokens via Amplify's default (localStorage) storage, so a sign-in
// on Astro is already visible here on the next check - it just needs something to trigger that
// check. Re-check on: a bfcache restore (back/forward), the tab regaining focus, and every
// Gatsby route change (defense-in-depth against other Gatsby-side caching/prefetch behavior we
// may not have hit yet). The min-interval keeps these from piling up when several fire together
// (e.g. a bfcache restore triggers both pageshow and a route update).
const MIN_RECHECK_INTERVAL_MS = 5_000;
let lastRecheckAt = 0;

const recheckAuth = () => {
  const now = Date.now();
  if (now - lastRecheckAt < MIN_RECHECK_INTERVAL_MS) return;
  lastRecheckAt = now;
  loadUser().catch((err) => {
    console.error("Failed to refresh auth session", err);
  });
};

export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = () => {
  recheckAuth();
};

export const onClientEntry = () => {
  if (!process.env.GATSBY_AWS_USER_CLIENT_ID) {
    throw new Error("Missing key in env: GATSBY_AWS_USER_CLIENT_ID");
  }
  if (!process.env.GATSBY_AWS_USER_POOL_ID) {
    throw new Error("Missing key in env: GATSBY_AWS_USER_POOL_ID");
  }

  // Configure cognito outside context so it persists. Uses Amplify's default (localStorage)
  // token storage - Astro is being switched to the same, since both apps run on the same
  // origin per environment and localStorage sharing there is automatic (no domain/cookie
  // config to keep in sync between the two apps/repos).
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: process.env.GATSBY_AWS_USER_POOL_ID,
        userPoolClientId: process.env.GATSBY_AWS_USER_CLIENT_ID,
      },
    },
  });

  window.addEventListener("pageshow", (event) => {
    if (event.persisted) recheckAuth();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") recheckAuth();
  });
};

const ASTRO_REDIRECT_STORAGE_KEY = "gatsby-astro-redirect-target";

export const onPreRouteUpdate: GatsbyBrowser["onPreRouteUpdate"] = ({ location }) => {
  const { pathname, search } = location;

  if (!isAstroPath(pathname)) {
    window.sessionStorage.removeItem(ASTRO_REDIRECT_STORAGE_KEY);
    return;
  }

  const target = pathname + search;

  // We've already tried redirecting to this exact URL once (e.g. this route update is a
  // re-fire from the previous redirect - via back/forward or a router re-entry). Bail out
  // instead of redirecting again so we can't get stuck in a loop.
  if (window.sessionStorage.getItem(ASTRO_REDIRECT_STORAGE_KEY) === target) return;

  window.sessionStorage.setItem(ASTRO_REDIRECT_STORAGE_KEY, target);

  // Replace (not assign .href) so this doesn't push a duplicate history entry on top
  // of the one Gatsby's router just pushed - otherwise the back button can land back
  // on this same dead entry and re-trigger the redirect.
  window.location.replace(target);
};

// fixes a scrolling bug in gatsby v5
// https://github.com/gatsbyjs/gatsby/issues/38201
export const shouldUpdateScroll: GatsbyBrowser["shouldUpdateScroll"] = ({ routerProps: { location }, getSavedScrollPosition }) => {
  // Check if there's a hash in the URL
  if (location.hash) {
    // Scroll to the top of the page

    // Use setTimeout to manage timing and ensure the DOM has updated
    setTimeout(() => {
      // Try to find the element by ID (without the #)
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        const top = window.scrollY + element.getBoundingClientRect().top - 50;
        window.scrollTo({ top, behavior: "smooth" });
      } else {
        // Fallback to the top if the element is not found
        window.scrollTo(0, 0);
      }
    }, 0);
  } else {
    // Handle the normal scroll behavior without a hash
    const currentPosition = getSavedScrollPosition(location);
    setTimeout(() => {
      window.scrollTo(...(currentPosition || [0, 0]));
    }, 0);
  }

  // Prevent Gatsby from handling scroll updates
  return false;
};
